import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Cloud, CloudOff, RefreshCw, Link2, Save } from "lucide-react";

export default function Settings() {
  const [drive, setDrive] = useState(null);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();

  const [settings, setSettings] = useState({
    brandName: "",
    slogan: "",
    logoUrl: "",
    invoiceAddress: "",
    invoicePhone: "",
    invoiceEmail: "",
    invoiceBank: "",
    invoiceNotes: "",
    exportPdfPaperSize: "A4",
    exportXlsxColumns: "all"
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchAll = async () => {
    const [{ data: d }, { data: e }, { data: s }] = await Promise.all([
      api.get("/drive/status"),
      api.get("/email/status"),
      api.get("/settings"),
    ]);
    setDrive(d);
    setEmail(e);
    if (s) setSettings((prev) => ({ ...prev, ...s }));
  };

  useEffect(() => {
    fetchAll();
    if (params.get("drive") === "connected") {
      toast.success("Google Drive berhasil terhubung");
    }
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", settings);
      window.dispatchEvent(new Event('settings-updated'));
      toast.success("Pengaturan berhasil disimpan");
    } catch (e) {
      toast.error("Gagal menyimpan pengaturan");
    } finally {
      setSavingSettings(false);
    }
  };

  const connect = async () => {
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konfigurasi Google Drive belum lengkap");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Putuskan koneksi Google Drive?")) return;
    await api.post("/drive/disconnect");
    toast.success("Google Drive diputuskan");
    fetchAll();
  };

  const sync = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/drive/sync");
      toast.success(`Sinkron berhasil: ${data.synced_orders} order`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal sinkronisasi");
    } finally {
      setLoading(false);
    }
  };

  if (!drive) return <div className="p-4 lg:p-10 text-stone-500">Memuat...</div>;

  return (
    <div className="p-4 lg:p-10 space-y-6 max-w-3xl" data-testid="settings-page">
      <header className="border-b border-[#E5DCC7] pb-6 clf-rule">
        <div className="clf-label mb-2">Konfigurasi</div>
        <h1 className="text-3xl tracking-tight font-medium">Pengaturan</h1>
        <p className="text-sm text-stone-500 mt-1">
          Konfigurasi layanan backup dan integrasi sistem.
        </p>
      </header>

      {/* Identitas Brand */}
      <section className="bg-white border border-[#E5DCC7]" data-testid="settings-brand">
        <div className="px-6 py-4 border-b border-[#E5DCC7] bg-stone-50">
          <h2 className="text-lg font-medium">Identitas Brand</h2>
          <p className="text-xs text-stone-500 mt-1">
            Pengaturan logo, nama brand, dan slogan toko Anda.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="clf-label block mb-1">Nama Brand</label>
            <input value={settings.brandName} onChange={e => setSettings({...settings, brandName: e.target.value})}
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
          </div>
          <div>
            <label className="clf-label block mb-1">Slogan</label>
            <input value={settings.slogan} onChange={e => setSettings({...settings, slogan: e.target.value})}
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
          </div>
          <div>
            <label className="clf-label block mb-1">Logo</label>
            {settings.logoUrl && (
              <div className="mb-2">
                <img src={settings.logoUrl} alt="Logo" className="h-16 w-16 object-contain border border-[#D9CDB5]" />
              </div>
            )}
            <input 
              type="file" 
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setSettings({...settings, logoUrl: reader.result});
                  };
                  reader.readAsDataURL(file);
                }
              }}
              className="w-full text-sm bg-white file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:bg-[#E5DCC7] file:text-stone-700 hover:file:bg-[#D9CDB5]" />
          </div>
          <button onClick={saveSettings} disabled={savingSettings}
            className="bg-[#16202B] text-white px-4 py-2 text-sm hover:bg-[#243345] flex items-center gap-2">
            <Save className="h-4 w-4" strokeWidth={1.5} /> Simpan
          </button>
        </div>
      </section>

      {/* Pengaturan Invoice */}
      <section className="bg-white border border-[#E5DCC7]" data-testid="settings-invoice">
        <div className="px-6 py-4 border-b border-[#E5DCC7] bg-stone-50">
          <h2 className="text-lg font-medium">Pengaturan Invoice</h2>
          <p className="text-xs text-stone-500 mt-1">
            Alamat toko dan catatan default yang akan tampil di invoice.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="clf-label block mb-1">Telepon / WA</label>
              <input value={settings.invoicePhone || ""} onChange={e => setSettings({...settings, invoicePhone: e.target.value.replace(/\D/g, '')})} inputMode="numeric"
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div>
              <label className="clf-label block mb-1">Email</label>
              <input value={settings.invoiceEmail || ""} onChange={e => setSettings({...settings, invoiceEmail: e.target.value})} type="email"
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
          </div>
          <div>
            <label className="clf-label block mb-1">Alamat Toko</label>
            <textarea value={settings.invoiceAddress || ""} onChange={e => setSettings({...settings, invoiceAddress: e.target.value})} rows={3}
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
          </div>
          <div>
            <label className="clf-label block mb-1">Rekening Pembayaran</label>
            <textarea value={settings.invoiceBank || ""} onChange={e => setSettings({...settings, invoiceBank: e.target.value})} rows={3} placeholder="BANK : BCA&#10;ACCOUNT : 123456789&#10;NAMA : CLF Tailor"
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
          </div>
          <div>
            <label className="clf-label block mb-1">Catatan Tambahan / Syarat Ketentuan</label>
            <textarea value={settings.invoiceNotes || ""} onChange={e => setSettings({...settings, invoiceNotes: e.target.value})} rows={3}
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
          </div>
          <button onClick={saveSettings} disabled={savingSettings}
            className="bg-[#16202B] text-white px-4 py-2 text-sm hover:bg-[#243345] flex items-center gap-2">
            <Save className="h-4 w-4" strokeWidth={1.5} /> Simpan
          </button>
        </div>
      </section>

      {/* Pengaturan Export */}
      <section className="bg-white border border-[#E5DCC7]" data-testid="settings-export">
        <div className="px-6 py-4 border-b border-[#E5DCC7] bg-stone-50">
          <h2 className="text-lg font-medium">Pengaturan Export (XLSX & PDF)</h2>
          <p className="text-xs text-stone-500 mt-1">
            Format kertas PDF dan kolom excel.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="clf-label block mb-1">Ukuran Kertas PDF</label>
            <select value={settings.exportPdfPaperSize} onChange={e => setSettings({...settings, exportPdfPaperSize: e.target.value})}
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900">
              <option value="A4">A4</option>
              <option value="A5">A5</option>
              <option value="Letter">Letter</option>
            </select>
          </div>
          <div>
            <label className="clf-label block mb-1">Kolom Export Excel</label>
            <select value={settings.exportXlsxColumns} onChange={e => setSettings({...settings, exportXlsxColumns: e.target.value})}
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900">
              <option value="all">Semua Kolom</option>
              <option value="measurements_only">Hanya Ukuran</option>
            </select>
          </div>
          <button onClick={saveSettings} disabled={savingSettings}
            className="bg-[#16202B] text-white px-4 py-2 text-sm hover:bg-[#243345] flex items-center gap-2">
            <Save className="h-4 w-4" strokeWidth={1.5} /> Simpan
          </button>
        </div>
      </section>

      {/* Backup Section */}
      <section className="bg-white border border-[#E5DCC7]" data-testid="settings-backup">
        <div className="px-6 py-4 border-b border-[#E5DCC7] bg-stone-50">
          <h2 className="text-lg font-medium">Backup & Sinkronisasi</h2>
          <p className="text-xs text-stone-500 mt-1">
            Hubungkan Google Drive untuk menyimpan data pesanan dan Email untuk pengiriman faktur.
          </p>
        </div>
        
        <div className="p-6 space-y-8">
          {/* Google Drive Sub-section */}
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 bg-stone-50 border border-[#E5DCC7] flex items-center justify-center">
                  {drive.connected ? (
                    <Cloud className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
                  ) : (
                    <CloudOff className="h-5 w-5 text-stone-400" strokeWidth={1.5} />
                  )}
                </div>
                <div>
                  <h3 className="text-[15px] font-medium text-stone-900">Google Drive</h3>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Backup otomatis invoice PDF & data ukuran ke folder "{drive.folder_name}"
                  </p>
                </div>
              </div>
              <span className={`text-xs font-medium px-2 py-1 border rounded-sm ${
                drive.connected ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-stone-100 text-stone-600 border-[#E5DCC7]"
              }`}>
                {drive.connected ? "Terhubung" : "Terputus"}
              </span>
            </div>

            {!drive.configured && (
              <div className="bg-amber-50 border border-amber-200 p-4 text-xs text-amber-900">
                <strong>Konfigurasi belum lengkap.</strong> Atur GOOGLE_CLIENT_ID,
                GOOGLE_CLIENT_SECRET, GOOGLE_DRIVE_REDIRECT_URI, dan FRONTEND_URL pada
                file <code>/app/backend/.env</code> lalu restart backend.
              </div>
            )}

            {drive.connected && drive.email && (
              <div className="text-xs text-stone-600 border-t border-[#E5DCC7] pt-3">
                Akun: <span className="font-mono">{drive.email}</span>
                {drive.last_sync && (
                  <span className="ml-4">
                    Sinkron terakhir: {new Date(drive.last_sync).toLocaleString("id-ID")}
                  </span>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              {drive.connected ? (
                <>
                  <button onClick={sync} disabled={loading} data-testid="drive-sync-btn"
                    className="bg-[#16202B] text-white px-4 py-2 text-sm hover:bg-[#243345] flex items-center gap-2 disabled:opacity-50">
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    Sinkronkan Sekarang
                  </button>
                  <button onClick={disconnect} data-testid="drive-disconnect-btn"
                    className="border border-[#D9CDB5] px-4 py-2 text-sm hover:bg-stone-100">
                    Putuskan
                  </button>
                </>
              ) : (
                <button onClick={connect} disabled={!drive.configured} data-testid="drive-connect-btn"
                  className="bg-[#A63D30] text-white px-5 py-2 text-sm hover:bg-[#8C3125] flex items-center gap-2 disabled:opacity-50">
                  <Link2 className="h-4 w-4" strokeWidth={1.5} />
                  Hubungkan Google Drive
                </button>
              )}
            </div>
          </div>

          <div className="h-px bg-[#E5DCC7] w-full" />

          {/* Email Sub-section */}
          <div className="space-y-3">
            <h3 className="text-[15px] font-medium text-stone-900">Email (Resend)</h3>
            <p className="text-xs text-stone-500">
              Untuk mengirim invoice via email. Atur RESEND_API_KEY di backend/.env
            </p>
            <div className="flex items-center justify-between border-t border-[#E5DCC7] pt-3">
              <div className="text-sm">
                Status:{" "}
                <span className={email?.configured ? "text-emerald-700 font-medium" : "text-stone-500"}>
                  {email?.configured ? "Aktif" : "Belum dikonfigurasi"}
                </span>
              </div>
              {email?.sender && (
                <div className="text-xs font-mono text-stone-500">{email.sender}</div>
              )}
            </div>
            {!email?.configured && (
              <div className="bg-amber-50 border border-amber-200 p-4 text-xs text-amber-900">
                Dapatkan API key gratis di{" "}
                <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">resend.com</a>,
                isi RESEND_API_KEY pada <code>/app/backend/.env</code>, lalu restart backend.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
